import React, { useContext, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Page from "../components/Page";
import { iconStroke } from "../config/config";
import {
  IconArmchair,
  IconBed,
  IconBoxSeam,
  IconCheck,
  IconChecks,
  IconClock,
  IconRefresh,
  IconX,
  IconChefHat,
  IconFilter,
  IconSettings,
  IconVolume,
  IconChecklist
} from "@tabler/icons-react";
import {
  getKitchenOrders,
  updateKitchenOrderItemStatus,
  completeAllKitchenOrderItems
} from "../controllers/kitchen.controller";
import { useKitchenDisplays } from "../controllers/kitchen_display.controller";
import { useCategories } from "../controllers/settings.controller";
import { toast } from "react-hot-toast";
import { SocketContext } from "../contexts/SocketContext";
import { initSocket } from "../utils/socket";
import { getUserDetailsInLocalStorage } from "../helpers/UserDetails";
import { useTheme } from "../contexts/ThemeContext";
import { groupItemsByPrinter, printKOT } from "../helpers/PrinterHelper";
import ApiClient from "../helpers/ApiClient";
import {
  playKitchenNewOrderSound,
  playItemCancelledSound,
  playKitchenOrderPickupReadySound,
  unlockAudio,
  isAudioUnlocked
} from "../helpers/SoundHelper";

export default function KitchenPage() {
  const { t } = useTranslation();
  const user = getUserDetailsInLocalStorage();
  const { socket, isSocketConnected } = useContext(SocketContext);
  const { theme } = useTheme();

  // Kitchen Displays (configured stations) and Tenant Categories
  const { data: kitchenDisplays, isLoading: isLoadingStations } = useKitchenDisplays();
  const { data: allCategoriesData } = useCategories();

  const [kitchenOrders, setKitchenOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Selected station filter ('all' or station ID)
  const [selectedStationId, setSelectedStationId] = useState(() => {
    return localStorage.getItem("kds_selected_station") || "all";
  });

  // Selected category filter ('all' or category ID)
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");

  // Sort order preference ('latest' or 'oldest')
  const [kitchenSortOrder, setKitchenSortOrder] = useState(() => {
    return localStorage.getItem("kds_sort_order") || "latest";
  });

  const handleKitchenSortChange = (newSort) => {
    setKitchenSortOrder(newSort);
    try {
      localStorage.setItem("kds_sort_order", newSort);
    } catch (e) {}
  };

  const [audioActive, setAudioActive] = useState(isAudioUnlocked());
  const [cancelModal, setCancelModal] = useState({
    isOpen: false,
    orderItemId: null,
    itemTitle: "",
    totalQty: 1,
    cancelQty: 1,
    reason: "Out of stock / ingredients finished",
    customReason: "",
    isSubmitting: false
  });

  // Active kitchen stations configured in settings
  const activeStations = useMemo(() => {
    return (kitchenDisplays || []).filter(
      (s) => s.is_active === 1 || s.is_active === true
    );
  }, [kitchenDisplays]);

  // Current selected kitchen station object
  const currentStation = useMemo(() => {
    if (selectedStationId === "all") return null;
    return activeStations.find((s) => String(s.id) === String(selectedStationId)) || null;
  }, [selectedStationId, activeStations]);

  // Save selected station preference
  const handleSelectStation = (stationId) => {
    setSelectedStationId(stationId);
    try {
      localStorage.setItem("kds_selected_station", stationId);
    } catch (e) {}
    // Reset category filter to 'all' when switching stations
    setSelectedCategoryId("all");
  };

  // Extract category ID safely from item
  const getItemCategoryId = (item) => {
    if (!item) return null;
    const cat = item.category ?? item.category_id;
    if (cat === null || cat === undefined || cat === "") return null;
    const num = Number(cat);
    return isNaN(num) ? cat : num;
  };

  // Group kitchen orders by table or room source
  function groupKitchenOrders(orders) {
    if (!orders) return [];
    const groupedOrders = [];
    for (const order of orders) {
      let orderIndex = -1;
      if (order.delivery_type === "room" && order.source) {
        orderIndex = groupedOrders.findIndex(
          (o) => o.delivery_type === "room" && o.source === order.source
        );
      } else if (order.table_id) {
        orderIndex = groupedOrders.findIndex((o) => o.table_id === order.table_id);
      } else {
        orderIndex = -1;
      }
      if (orderIndex === -1) {
        groupedOrders.push({ ...order, items: [...(order.items || [])] });
      } else {
        groupedOrders[orderIndex].items.push(...(order.items || []));
        if (!String(groupedOrders[orderIndex].token_no).includes(String(order.token_no))) {
          groupedOrders[orderIndex].token_no = `${groupedOrders[orderIndex].token_no}, ${order.token_no}`;
        }
      }
    }
    return groupedOrders;
  }

  // Load kitchen orders from API
  const _init = async () => {
    try {
      const res = await getKitchenOrders();
      if (res.status === 200) {
        const orders = res?.data || [];
        setKitchenOrders(groupKitchenOrders(orders));
      }

      // Fetch categories for printer info
      try {
        const catRes = await ApiClient.get("/settings/categories");
        setCategories(catRes.data || []);
      } catch (catErr) {
        console.warn("Could not load categories list:", catErr);
      }
    } catch (error) {
      console.error(error);
      toast.dismiss();
      toast.error(t("kitchen.error_loading_orders"));
    } finally {
      setIsLoading(false);
    }
  };

  const btnRefresh = async () => {
    try {
      toast.loading(t("kitchen.loading_message"));
      const res = await getKitchenOrders();
      toast.dismiss();
      if (res.status === 200) {
        const orders = res?.data || [];
        setKitchenOrders(groupKitchenOrders(orders));
        return orders;
      }
    } catch (error) {
      toast.dismiss();
      console.error(error);
      toast.error(t("kitchen.error_loading_orders"));
    }
  };

  // Available categories for the currently selected station
  const availableCategories = useMemo(() => {
    const allCats = allCategoriesData && allCategoriesData.length > 0 ? allCategoriesData : categories;
    if (!currentStation || !Array.isArray(currentStation.categories) || currentStation.categories.length === 0) {
      return allCats;
    }
    const stationCatIds = currentStation.categories.map((c) => Number(c));
    return allCats.filter((c) => stationCatIds.includes(Number(c.id)));
  }, [allCategoriesData, categories, currentStation]);

  // Compute pending item counts per category (only items that are not completed/delivered/cancelled)
  const categoryPendingCounts = useMemo(() => {
    const counts = { all: 0 };

    for (const order of kitchenOrders) {
      for (const item of order.items || []) {
        const isPending = !["completed", "delivered", "cancelled"].includes(item.status);
        if (!isPending) continue;

        // If a specific station is selected, check station mapping
        if (currentStation && Array.isArray(currentStation.categories) && currentStation.categories.length > 0) {
          const itemCatId = getItemCategoryId(item);
          const mappedIds = currentStation.categories.map((c) => Number(c));
          if (!itemCatId || !mappedIds.includes(Number(itemCatId))) {
            continue; // Skip items outside this station
          }
        }

        counts.all = (counts.all || 0) + (Number(item.quantity) || 1);

        const catId = getItemCategoryId(item);
        if (catId !== null) {
          const key = String(catId);
          counts[key] = (counts[key] || 0) + (Number(item.quantity) || 1);
        } else {
          counts.uncategorized = (counts.uncategorized || 0) + (Number(item.quantity) || 1);
        }
      }
    }

    return counts;
  }, [kitchenOrders, currentStation]);

  // Filtered orders & items based on selected station and selected category
  const displayedOrders = useMemo(() => {
    const result = [];

    for (const order of kitchenOrders) {
      // Filter items of this order based on station + category
      const visibleItems = (order.items || []).filter((item) => {
        // Station filter: if station has assigned categories, item category must match
        if (currentStation && Array.isArray(currentStation.categories) && currentStation.categories.length > 0) {
          const itemCatId = getItemCategoryId(item);
          const mappedIds = currentStation.categories.map((c) => Number(c));
          if (!itemCatId || !mappedIds.includes(Number(itemCatId))) {
            return false;
          }
        }

        // Category filter
        if (selectedCategoryId !== "all") {
          const itemCatId = getItemCategoryId(item);
          if (selectedCategoryId === "uncategorized") {
            if (itemCatId !== null) return false;
          } else {
            if (itemCatId === null || String(itemCatId) !== String(selectedCategoryId)) {
              return false;
            }
          }
        }

        return true;
      });

      // Hide card if no items remain after filtering or all items are finished
      const hasActiveItems = visibleItems.some(
        (i) => i.status !== "completed" && i.status !== "delivered"
      );

      if (visibleItems.length > 0 && hasActiveItems) {
        result.push({
          ...order,
          items: visibleItems,
        });
      }
    }

    // Sort orders by timestamp / ID based on kitchenSortOrder (default: latest first)
    const getOrderSortStats = (ord) => {
      let maxTime = 0;
      let maxId = Number(ord.id || ord.order_id || 0) || 0;

      if (ord.date) {
        const t = new Date(ord.date).getTime();
        if (!isNaN(t) && t > maxTime) maxTime = t;
      }
      if (ord.created_at) {
        const t = new Date(ord.created_at).getTime();
        if (!isNaN(t) && t > maxTime) maxTime = t;
      }

      for (const item of (ord.items || [])) {
        if (item.date) {
          const t = new Date(item.date).getTime();
          if (!isNaN(t) && t > maxTime) maxTime = t;
        }
        if (item.created_at) {
          const t = new Date(item.created_at).getTime();
          if (!isNaN(t) && t > maxTime) maxTime = t;
        }
        const oId = Number(item.order_id || 0) || 0;
        if (oId > maxId) maxId = oId;
      }

      return { maxTime, maxId };
    };

    result.sort((a, b) => {
      const statsA = getOrderSortStats(a);
      const statsB = getOrderSortStats(b);

      if (kitchenSortOrder === "latest") {
        if (statsB.maxTime !== statsA.maxTime) {
          return statsB.maxTime - statsA.maxTime;
        }
        return statsB.maxId - statsA.maxId;
      } else {
        if (statsA.maxTime !== statsB.maxTime) {
          return statsA.maxTime - statsB.maxTime;
        }
        return statsA.maxId - statsB.maxId;
      }
    });

    return result;
  }, [kitchenOrders, currentStation, selectedCategoryId, kitchenSortOrder]);

  // Cancel item modal handlers
  const btnShowCancelModal = (orderItemId, itemTitle, totalQty = 1) => {
    setCancelModal({
      isOpen: true,
      orderItemId,
      itemTitle,
      totalQty: Number(totalQty) || 1,
      cancelQty: 1,
      reason: "Out of stock / ingredients finished",
      customReason: "",
      isSubmitting: false,
    });
  };

  const btnConfirmCancelItem = async () => {
    if (!cancelModal.orderItemId) return;
    const finalReason =
      cancelModal.reason === "Other Reason"
        ? cancelModal.customReason || "Cancelled by kitchen"
        : cancelModal.reason || "Cancelled by kitchen";

    try {
      setCancelModal((prev) => ({ ...prev, isSubmitting: true }));
      toast.loading(t("kitchen.loading_message"));

      const res = await updateKitchenOrderItemStatus(
        cancelModal.orderItemId,
        "cancelled",
        finalReason,
        cancelModal.cancelQty
      );

      toast.dismiss();
      if (res.status === 200) {
        setCancelModal((prev) => ({ ...prev, isOpen: false }));
        unlockAudio();
        setAudioActive(true);
        playItemCancelledSound({ itemTitle: cancelModal.itemTitle, reason: finalReason });
        toast.success(`Cancelled ${cancelModal.cancelQty} x ${cancelModal.itemTitle}`);
        await btnRefresh();
        sendOrderUpdateEvent();
      }
    } catch (error) {
      toast.dismiss();
      const message = error?.response?.data?.message || t("kitchen.error_processing_request");
      toast.error(message);
      setCancelModal((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  useEffect(() => {
    _init();
  }, []);

  useEffect(() => {
    if (!socket) return;

    if (user?.tenant_id) {
      socket.emit("authenticate", user.tenant_id);
    }

    const onConnect = () => {
      if (user?.tenant_id) {
        socket.emit("authenticate", user.tenant_id);
      }
    };

    const handleNewOrder = async (payload) => {
      console.log("New Order Received in Kitchen:", payload);
      unlockAudio();
      setAudioActive(true);
      playKitchenNewOrderSound({
        tokenNo: payload?.tokenNo,
        tableTitle: payload?.tableTitle || payload?.table_title,
        tableId: payload?.tableId,
        deliveryType: payload?.deliveryType,
        source: payload?.source,
      });

      const tokenText = payload?.tokenNo
        ? `Token #${payload.tokenNo}`
        : payload?.orderId
        ? `Order #${payload.orderId}`
        : "";
      const sourceText =
        payload?.source ||
        (payload?.deliveryType === "room"
          ? "Room Service"
          : payload?.tableId
          ? `Table ${payload.tableId}`
          : "New Order");

      toast.custom(
        (toastItem) => (
          <div
            className={`${
              toastItem.visible ? "animate-enter" : "animate-leave"
            } max-w-md w-full bg-emerald-600 shadow-2xl rounded-2xl pointer-events-auto flex ring-2 ring-emerald-400 p-4 text-white items-center justify-between`}
          >
            <div className="flex items-center gap-3">
              <span className="text-3xl animate-bounce">🍳</span>
              <div>
                <p className="text-base font-extrabold uppercase tracking-wide">New Order for Kitchen!</p>
                <p className="text-sm font-medium opacity-90">
                  {sourceText} {tokenText ? `(${tokenText})` : ""}
                </p>
              </div>
            </div>
            <button
              onClick={() => toast.dismiss(toastItem.id)}
              className="ml-3 text-white text-xl font-bold bg-white/20 hover:bg-white/30 rounded-full w-8 h-8 flex items-center justify-center"
            >
              ✕
            </button>
          </div>
        ),
        { duration: 8000 }
      );

      const updatedOrders = await btnRefresh();

      if (payload && payload.orderId && updatedOrders) {
        const newOrder = updatedOrders.find((o) => o.id == payload.orderId);
        if (newOrder) {
          const printerGroups = groupItemsByPrinter(newOrder.items, categories);
          if (localStorage.getItem("kdsAutoPrint") !== "false") {
            printKOT(printerGroups, {
              tableId: newOrder.table_id,
              table_title: newOrder.table_title,
              tokenNo: newOrder.token_no,
              orderId: newOrder.id,
              customerName: newOrder.customer_name,
            });
          }
        }
      }
    };

    const handleItemCancelled = (payload) => {
      console.log("Kitchen Display received cancellation alert:", payload);
      unlockAudio();
      setAudioActive(true);
      playItemCancelledSound({
        reason: payload?.cancelReason,
        itemTitle: payload?.itemTitle,
        tableTitle: payload?.tableTitle,
      });

      toast.custom(
        (toastItem) => (
          <div
            className={`${
              toastItem.visible ? "animate-enter" : "animate-leave"
            } max-w-md w-full bg-red-600 shadow-2xl rounded-2xl pointer-events-auto flex ring-2 ring-red-400 p-4 text-white items-center justify-between`}
          >
            <div className="flex items-center gap-3">
              <span className="text-3xl animate-bounce">🚨</span>
              <div>
                <p className="text-base font-extrabold uppercase tracking-wide">Item Cancelled by Staff!</p>
                <p className="text-sm font-medium opacity-90">{payload?.cancelReason || "Cancelled in POS/Orders"}</p>
              </div>
            </div>
            <button
              onClick={() => toast.dismiss(toastItem.id)}
              className="ml-3 text-white text-xl font-bold bg-white/20 hover:bg-white/30 rounded-full w-8 h-8 flex items-center justify-center"
            >
              ✕
            </button>
          </div>
        ),
        { duration: 9000 }
      );

      btnRefresh();
    };

    const handleOrderUpdate = (payload) => {
      if (payload?.type === "item_cancelled" || payload?.type === "order_cancelled") {
        handleItemCancelled(payload);
      } else {
        btnRefresh();
      }
    };

    socket.on("connect", onConnect);
    socket.on("new_order", handleNewOrder);
    socket.on("new_qrorder", handleNewOrder);
    socket.on("new_order_notification", handleNewOrder);
    socket.on("order_update", handleOrderUpdate);
    socket.on("order_item_cancelled", handleItemCancelled);
    socket.on("order_cancelled", handleItemCancelled);

    return () => {
      socket.off("connect", onConnect);
      socket.off("new_order", handleNewOrder);
      socket.off("new_qrorder", handleNewOrder);
      socket.off("new_order_notification", handleNewOrder);
      socket.off("order_update", handleOrderUpdate);
      socket.off("order_item_cancelled", handleItemCancelled);
      socket.off("order_cancelled", handleItemCancelled);
    };
  }, [socket, isSocketConnected, categories]);

  const sendOrderUpdateEvent = () => {
    const userDetails = getUserDetailsInLocalStorage();
    if (isSocketConnected) {
      socket.emit("order_update_backend", {}, userDetails?.tenant_id);
    } else {
      initSocket();
      socket.emit("order_update_backend", {}, userDetails?.tenant_id);
    }
  };

  const btnStartPreparingOrderItem = async (orderItemId) => {
    try {
      toast.loading(t("kitchen.loading_message"));
      const res = await updateKitchenOrderItemStatus(orderItemId, "preparing");
      toast.dismiss();
      if (res.status === 200) {
        await btnRefresh();
        sendOrderUpdateEvent();
        toast.success(res.data.message);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t("kitchen.error_processing_request");
      toast.dismiss();
      console.error(error);
      toast.error(message);
    }
  };

  // Complete a single item: triggers buzzer + voice speech immediately
  const btnCompletePreparingOrderItem = async (orderItemId, item, order) => {
    try {
      toast.loading(t("kitchen.loading_message"));
      const res = await updateKitchenOrderItemStatus(orderItemId, "completed");
      toast.dismiss();
      if (res.status === 200) {
        unlockAudio();
        setAudioActive(true);
        // Announce ready sound
        playKitchenOrderPickupReadySound({
          tableTitle: order?.table_title,
          roomName: order?.source,
          deliveryType: order?.delivery_type,
          tokenNo: order?.token_no,
          itemTitle: item?.item_title || "Item",
        });

        sendOrderUpdateEvent();
        await btnRefresh();
        toast.success(res.data.message || "Item completed!");
      }
    } catch (error) {
      const message = error?.response?.data?.message || t("kitchen.error_processing_request");
      toast.dismiss();
      console.error(error);
      toast.error(message);
    }
  };

  // Complete all items in an entire order card
  const btnCompleteAllOrderItems = async (order) => {
    try {
      toast.loading("Marking order items complete...");
      // Distinct order IDs for grouped table items
      const orderIds = [
        ...new Set(
          (order.items || [])
            .map((i) => i.order_id || order.id)
            .filter(Boolean)
        ),
      ];

      for (const ordId of orderIds) {
        await completeAllKitchenOrderItems(ordId);
      }

      toast.dismiss();
      unlockAudio();
      setAudioActive(true);

      // Play chime & voice speech announcement
      playKitchenOrderPickupReadySound({
        tableTitle: order?.table_title,
        roomName: order?.source,
        deliveryType: order?.delivery_type,
        tokenNo: order?.token_no,
        itemTitle: "All items",
      });

      sendOrderUpdateEvent();
      await btnRefresh();
      toast.success("Order marked ready for pickup!");
    } catch (error) {
      toast.dismiss();
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to complete all items");
    }
  };

  return (
    <Page>
      {/* Top Bar Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
            <IconChefHat size={26} stroke={iconStroke} />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              {t("kitchen.title")}
              {currentStation && (
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                  {currentStation.name}
                </span>
              )}
            </h3>
            <p className="text-xs text-gray-400">Real-time KDS with Station Routing & Voice Alerts</p>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          {/* Sort Order Toggle Pills */}
          <div className="flex items-center bg-gray-100 dark:bg-neutral-800 p-0.5 rounded-xl border border-gray-200 dark:border-neutral-700 shadow-xs">
            <button
              type="button"
              onClick={() => handleKitchenSortChange("latest")}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition ${
                kitchenSortOrder === "latest"
                  ? "bg-amber-500 text-white shadow-xs"
                  : "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              }`}
              title="Show latest orders first"
            >
              ⚡ Latest First
            </button>
            <button
              type="button"
              onClick={() => handleKitchenSortChange("oldest")}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition ${
                kitchenSortOrder === "oldest"
                  ? "bg-amber-500 text-white shadow-xs"
                  : "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              }`}
              title="Show oldest orders first"
            >
              ⏳ Oldest First
            </button>
          </div>

          {/* Refresh Button */}
          <button
            onClick={btnRefresh}
            className={`border-none shadow-xs transition active:scale-95 text-gray-600 dark:text-gray-200 px-3 py-2 flex items-center gap-1.5 text-xs font-semibold rounded-xl ${
              theme === "black"
                ? "bg-neutral-800 hover:bg-neutral-700"
                : "bg-gray-100 hover:bg-gray-200"
            }`}
          >
            <IconRefresh size={18} stroke={iconStroke} /> {t("kitchen.refresh")}
          </button>

          {/* Sound / Buzzer Test Button */}
          <button
            type="button"
            onClick={() => {
              unlockAudio();
              setAudioActive(true);
              playKitchenOrderPickupReadySound({
                tableTitle: "Table 1",
                itemTitle: "Test Dish",
              });
              toast.success("🔊 Kitchen audio active & tested!");
            }}
            className={`px-3.5 py-2 rounded-xl flex items-center gap-2 text-xs font-bold transition border shadow-xs ${
              audioActive
                ? "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 hover:bg-emerald-100"
                : "bg-amber-100 text-amber-900 border-amber-400 animate-pulse hover:bg-amber-200"
            }`}
            title="Click to test buzzer sound and ensure audio is unmuted"
          >
            <IconVolume size={18} stroke={iconStroke} />
            {audioActive ? "Sound Active" : "Tap to Enable Audio"}
          </button>

          {/* Link to Kitchen Displays Settings */}
          <Link
            to="/dashboard/settings/kitchen-displays"
            className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-neutral-800 rounded-xl transition"
            title="Configure Kitchen Display Stations & Category Assignments"
          >
            <IconSettings size={20} stroke={iconStroke} />
          </Link>
        </div>
      </div>

      {/* Browser Autoplay Requirement Notice Banner */}
      {!audioActive && (
        <div
          onClick={() => {
            unlockAudio();
            setAudioActive(true);
            playKitchenOrderPickupReadySound({ tableTitle: "Kitchen Display" });
            toast.success("🔊 Kitchen sound alerts unmuted!");
          }}
          className="mt-3 cursor-pointer bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:opacity-95 transition text-white px-4 py-3 rounded-2xl flex items-center justify-between shadow-lg ring-2 ring-amber-300/50"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl animate-bounce">🔔</span>
            <div>
              <p className="font-extrabold text-sm">Browser requires 1 tap to play pickup & cancellation voice alerts</p>
              <p className="text-xs opacity-95">Tap anywhere on this banner to activate real-time chimes and voice announcements</p>
            </div>
          </div>
          <button type="button" className="bg-white text-amber-900 font-extrabold text-xs px-3.5 py-1.5 rounded-xl shadow">
            Enable Audio Now
          </button>
        </div>
      )}

      {/* Kitchen Station Switcher Bar */}
      {activeStations.length > 0 && (
        <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider shrink-0 flex items-center gap-1">
            <IconChefHat size={16} stroke={iconStroke} /> Kitchen:
          </span>

          <button
            type="button"
            onClick={() => handleSelectStation("all")}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition active:scale-95 shrink-0 ${
              selectedStationId === "all"
                ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                : "bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-neutral-800 dark:text-gray-300 dark:hover:bg-neutral-700"
            }`}
          >
            All Kitchens
          </button>

          {activeStations.map((station) => {
            const isSelected = String(station.id) === String(selectedStationId);
            const catCount = Array.isArray(station.categories) ? station.categories.length : 0;

            return (
              <button
                key={station.id}
                type="button"
                onClick={() => handleSelectStation(station.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition active:scale-95 shrink-0 flex items-center gap-1.5 ${
                  isSelected
                    ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-neutral-800 dark:text-gray-300 dark:hover:bg-neutral-700"
                }`}
              >
                <span>{station.name}</span>
                {catCount > 0 && (
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                      isSelected
                        ? "bg-white/20 text-white"
                        : "bg-gray-200 text-gray-600 dark:bg-neutral-700 dark:text-gray-300"
                    }`}
                  >
                    {catCount} {catCount === 1 ? "cat" : "cats"}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Category Filter Pills Bar */}
      <div className="mt-3.5 flex items-center gap-2 overflow-x-auto pb-1.5 scrollbar-none border-b border-gray-100 dark:border-neutral-800">
        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider shrink-0 flex items-center gap-1">
          <IconFilter size={15} stroke={iconStroke} /> Category:
        </span>

        {/* 'All' Category Pill */}
        <button
          type="button"
          onClick={() => setSelectedCategoryId("all")}
          className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition active:scale-95 shrink-0 flex items-center gap-1.5 ${
            selectedCategoryId === "all"
              ? "bg-restro-green text-white shadow-sm"
              : "bg-gray-50 hover:bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-gray-300 dark:hover:bg-neutral-700 border border-gray-200 dark:border-neutral-700"
          }`}
        >
          <span>All Categories</span>
          {(categoryPendingCounts.all || 0) > 0 && (
            <span
              className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${
                selectedCategoryId === "all"
                  ? "bg-white/25 text-white"
                  : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
              }`}
            >
              {categoryPendingCounts.all}
            </span>
          )}
        </button>

        {/* Specific Category Pills */}
        {availableCategories.map((cat) => {
          const isSelected = String(cat.id) === String(selectedCategoryId);
          const pendingCount = categoryPendingCounts[String(cat.id)] || 0;

          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => setSelectedCategoryId(String(cat.id))}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition active:scale-95 shrink-0 flex items-center gap-1.5 ${
                isSelected
                  ? "bg-restro-green text-white shadow-sm"
                  : "bg-gray-50 hover:bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-gray-300 dark:hover:bg-neutral-700 border border-gray-200 dark:border-neutral-700"
              }`}
            >
              <span>{cat.title}</span>
              {pendingCount > 0 && (
                <span
                  className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${
                    isSelected
                      ? "bg-white/25 text-white"
                      : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                  }`}
                >
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}

        {/* Uncategorized items pill if any exist */}
        {(categoryPendingCounts.uncategorized || 0) > 0 && (
          <button
            type="button"
            onClick={() => setSelectedCategoryId("uncategorized")}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition active:scale-95 shrink-0 flex items-center gap-1.5 ${
              selectedCategoryId === "uncategorized"
                ? "bg-restro-green text-white shadow-sm"
                : "bg-gray-50 hover:bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-gray-300 dark:hover:bg-neutral-700 border border-gray-200 dark:border-neutral-700"
            }`}
          >
            <span>General / Other</span>
            <span
              className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${
                selectedCategoryId === "uncategorized"
                  ? "bg-white/25 text-white"
                  : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
              }`}
            >
              {categoryPendingCounts.uncategorized}
            </span>
          </button>
        )}
      </div>

      {isLoading && (
        <div className="py-12 flex justify-center items-center">
          <p className="text-sm font-semibold text-gray-500 animate-pulse">{t("kitchen.loading_message")}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && displayedOrders.length === 0 && (
        <div className="w-full h-[calc(100vh-280px)] flex gap-4 flex-col items-center justify-center">
          <img
            src="/assets/illustrations/kitchen-order-not-found.webp"
            alt={t("kitchen.no_orders_img_alt")}
            className="w-1/2 md:w-56 opacity-80"
          />
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-300 font-bold text-base">
              {t("kitchen.no_pending_orders")}
            </p>
            {selectedCategoryId !== "all" && (
              <p className="text-xs text-gray-400 mt-1">
                No active orders in selected category.{" "}
                <button
                  type="button"
                  onClick={() => setSelectedCategoryId("all")}
                  className="text-emerald-600 underline font-semibold"
                >
                  Show all categories
                </button>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Orders Grid */}
      {!isLoading && displayedOrders.length > 0 && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
          {displayedOrders.map((order, orderIdx) => {
            const {
              id,
              delivery_type,
              customer_type,
              customer_name,
              table_id,
              table_title,
              floor,
              token_no,
              items,
              source,
            } = order;

            const isRoom =
              delivery_type === "room" ||
              !!source ||
              (table_title && table_title.toLowerCase().startsWith("room"));
            const roomDisplay =
              source || (table_title ? table_title.replace(/^room\s*/i, "") : "");
            const diningTitle = isRoom
              ? roomDisplay
                ? roomDisplay.toLowerCase().startsWith("room")
                  ? roomDisplay
                  : `Room ${roomDisplay}`
                : "Room Order"
              : table_id
              ? `${table_title}`
              : `${(delivery_type || "").toUpperCase()} ${(customer_type || "").toUpperCase()}`.trim();
            const displayFloor = isRoom ? floor || "Room Service" : floor;

            const pendingItemsCount = items.filter(
              (i) => i.status !== "completed" && i.status !== "delivered" && i.status !== "cancelled"
            ).length;

            return (
              <div
                key={orderIdx}
                className="border rounded-2xl p-4 flex flex-col justify-between border-emerald-500/30 bg-white dark:bg-neutral-800 shadow-sm hover:shadow-md transition"
              >
                {/* Header */}
                <div>
                  <div className="flex items-center justify-between gap-2 pb-3 border-b border-gray-100 dark:border-neutral-700">
                    <div className="flex items-center gap-2.5">
                      <div className="flex w-10 h-10 rounded-xl items-center justify-center bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-200">
                        {isRoom ? (
                          <IconBed size={22} stroke={iconStroke} className="text-amber-500" />
                        ) : delivery_type === "dinein" ? (
                          <IconArmchair size={22} stroke={iconStroke} className="text-emerald-600" />
                        ) : (
                          <IconBoxSeam size={22} stroke={iconStroke} />
                        )}
                      </div>
                      <div>
                        <p className="font-extrabold text-base text-gray-800 dark:text-white leading-tight">
                          {diningTitle}
                        </p>
                        {displayFloor && <p className="text-xs text-gray-400 font-medium">{displayFloor}</p>}
                      </div>
                    </div>

                    <div className="text-end">
                      <span className="inline-block px-2.5 py-1 rounded-lg text-xs font-extrabold bg-emerald-50 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/50">
                        {t("kitchen.token")} #{token_no}
                      </span>
                    </div>
                  </div>

                  {/* Order Items List */}
                  <div className="mt-3 flex flex-col divide-y divide-gray-100 dark:divide-neutral-700">
                    {items.map((item, itemIdx) => {
                      const {
                        id: orderItemId,
                        item_title,
                        variant_title,
                        category_title,
                        quantity,
                        status,
                        addons,
                        notes,
                      } = item;

                      const addonsText =
                        addons?.length > 0
                          ? addons.map((a) => a?.title).filter(Boolean).join(", ")
                          : null;

                      const isItemDone = status === "completed" || status === "delivered";
                      const isItemCancelled = status === "cancelled";

                      return (
                        <div
                          key={itemIdx}
                          className={`py-2.5 flex items-start justify-between gap-2 transition ${
                            isItemDone ? "opacity-40" : ""
                          }`}
                        >
                          {/* Item Details */}
                          <div className="flex-1 pr-2">
                            <div className="flex items-center gap-1.5">
                              {status === "preparing" && (
                                <IconClock size={16} stroke={iconStroke} className="text-amber-500 shrink-0 animate-spin" />
                              )}
                              {isItemDone && (
                                <IconCheck size={16} stroke={iconStroke} className="text-emerald-500 shrink-0" />
                              )}
                              {isItemCancelled && (
                                <IconX size={16} stroke={iconStroke} className="text-red-500 shrink-0" />
                              )}

                              <p className="font-bold text-sm text-gray-800 dark:text-gray-100">
                                <span className="text-emerald-600 dark:text-emerald-400 font-extrabold mr-1">
                                  {quantity}x
                                </span>
                                {item_title} {variant_title ? `(${variant_title})` : ""}
                              </p>
                            </div>

                            {/* Category Badge if visible */}
                            {category_title && (
                              <span className="inline-block mt-0.5 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                                {category_title}
                              </span>
                            )}

                            {addonsText && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mt-0.5">
                                + {addonsText}
                              </p>
                            )}

                            {notes && (
                              <p className="text-xs text-red-600 dark:text-red-400 font-semibold bg-red-50 dark:bg-red-950/30 px-2 py-0.5 rounded-md mt-1 inline-block">
                                Note: {notes}
                              </p>
                            )}
                          </div>

                          {/* Item Actions */}
                          <div className="flex items-center gap-1 shrink-0 pt-0.5">
                            {(status === "pending" || status === "created") && (
                              <button
                                onClick={() => btnStartPreparingOrderItem(orderItemId)}
                                className="px-2.5 py-1 text-xs font-bold rounded-lg border border-amber-500/40 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition active:scale-95"
                              >
                                {t("kitchen.start_making")}
                              </button>
                            )}

                            {status === "preparing" && (
                              <button
                                onClick={() => btnCompletePreparingOrderItem(orderItemId, item, order)}
                                className="px-2.5 py-1 text-xs font-bold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs transition active:scale-95"
                              >
                                {t("kitchen.complete")}
                              </button>
                            )}

                            {!isItemDone && !isItemCancelled && (
                              <button
                                type="button"
                                onClick={() =>
                                  btnShowCancelModal(
                                    orderItemId,
                                    `${item_title} ${variant_title || ""}`.trim(),
                                    quantity
                                  )
                                }
                                title="Cancel this item"
                                className="text-gray-400 hover:text-red-500 p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition"
                              >
                                <IconX size={16} stroke={iconStroke} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Card Footer: Complete All Ready Button */}
                {pendingItemsCount > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-neutral-700 flex items-center justify-between gap-2">
                    <span className="text-[11px] font-medium text-gray-400">
                      {pendingItemsCount} item{pendingItemsCount > 1 ? "s" : ""} pending
                    </span>
                    <button
                      type="button"
                      onClick={() => btnCompleteAllOrderItems(order)}
                      className="px-3 py-1.5 text-xs font-bold rounded-xl bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white shadow-xs transition flex items-center gap-1.5"
                    >
                      <IconCheck size={16} stroke={iconStroke} />
                      Complete All Ready
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Cancel Item Modal */}
      {cancelModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-fade-in">
          <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-100 dark:border-neutral-700 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-neutral-700">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <IconX size={24} stroke={2.5} />
                <h4 className="text-lg font-bold">Cancel Order Item</h4>
              </div>
              <button
                type="button"
                onClick={() => setCancelModal((prev) => ({ ...prev, isOpen: false }))}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
              >
                <IconX size={20} />
              </button>
            </div>

            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Item to cancel:</p>
              <p className="text-base font-bold text-gray-800 dark:text-white mt-0.5">
                {cancelModal.itemTitle}
              </p>
            </div>

            {cancelModal.totalQty > 1 && (
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                  Quantity to Cancel (Total: {cancelModal.totalQty})
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max={cancelModal.totalQty}
                    value={cancelModal.cancelQty}
                    onChange={(e) => {
                      const val = Math.max(
                        1,
                        Math.min(cancelModal.totalQty, parseInt(e.target.value) || 1)
                      );
                      setCancelModal((prev) => ({ ...prev, cancelQty: val }));
                    }}
                    className="w-24 px-3 py-2 border rounded-xl dark:bg-neutral-700 dark:border-neutral-600 text-center font-bold text-lg"
                  />
                  <span className="text-xs text-gray-500">out of {cancelModal.totalQty}</span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                Reason for Cancellation
              </label>
              <select
                value={cancelModal.reason}
                onChange={(e) => setCancelModal((prev) => ({ ...prev, reason: e.target.value }))}
                className="w-full px-3 py-2.5 border rounded-xl dark:bg-neutral-700 dark:border-neutral-600 text-sm font-medium"
              >
                <option value="Out of stock / ingredients finished">Out of stock / ingredients finished</option>
                <option value="Customer changed mind">Customer changed mind</option>
                <option value="Kitchen equipment issue">Kitchen equipment issue</option>
                <option value="Accidental order / wrong entry">Accidental order / wrong entry</option>
                <option value="Other Reason">Other Reason</option>
              </select>
            </div>

            {cancelModal.reason === "Other Reason" && (
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                  Specify Reason
                </label>
                <textarea
                  rows="2"
                  value={cancelModal.customReason}
                  onChange={(e) =>
                    setCancelModal((prev) => ({ ...prev, customReason: e.target.value }))
                  }
                  placeholder="Enter reason..."
                  className="w-full px-3 py-2 border rounded-xl dark:bg-neutral-700 dark:border-neutral-600 text-sm"
                />
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setCancelModal((prev) => ({ ...prev, isOpen: false }))}
                disabled={cancelModal.isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-neutral-700 rounded-xl transition"
              >
                Close
              </button>
              <button
                type="button"
                onClick={btnConfirmCancelItem}
                disabled={cancelModal.isSubmitting}
                className="px-5 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 active:scale-95 rounded-xl shadow transition flex items-center gap-2"
              >
                {cancelModal.isSubmitting ? "Cancelling..." : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
